import React, { FunctionComponent, useState } from 'react';
import styled from 'styled-components';
import { isEmpty, isNonEmptyString } from 'ts-utils';
import { CopyContainer } from '../../styles/CopyContainer';
import {
  OptionButtonInput,
  OptionButtonLabel,
  OptionButtonWidth,
  OptionFieldset,
} from '../../styles/OptionButton';
import { QuestionWrapper } from '../../styles/Containers';
import { BodySM, Heading3 } from '../../styles/Typography';

export type NumberedChoice = {
  value: string;
  label: string;
};

export type NumberedSingleSelectLayoutConfig = {
  heading?: string;
  body?: string[];
  /** Each choice is shown with its zero-based index as a left-aligned number, so order matters. */
  choices: NumberedChoice[];
  toastMessage?: string;
};

type Props = NumberedSingleSelectLayoutConfig & {
  onSelected?: (value: string) => void;
  onShowToast?: () => void;
  noWrapper?: boolean;
};

export const NumberedSingleSelectLayout: FunctionComponent<Props> = (props) => {
  const { heading, body, choices, onSelected, onShowToast, toastMessage, noWrapper } = props;
  const [selected, setSelected] = useState<string>();

  function onSelectionChange(event: React.FormEvent<HTMLFieldSetElement>) {
    const value = (event.target as HTMLInputElement).value;
    setSelected(value);
    if (isNonEmptyString(toastMessage)) onShowToast?.();
    onSelected?.(value);
  }

  const content = (
    <div style={{ margin: 'auto' }}>
      <CopyContainer>
        {!isEmpty(heading) && <Heading3>{heading}</Heading3>}
        {body?.map((text, index) => (
          <BodySM key={index} data-test="surveyQuestion">
            {text}
          </BodySM>
        ))}
      </CopyContainer>
      <OptionFieldset onChange={onSelectionChange}>
        {choices.map((choice, index) => {
          const inputId = choice.value.replace(/\s+/g, '');
          const isSelected = selected === choice.value;
          return (
            <NumberedOptionLabel
              key={choice.value}
              className={isSelected ? 'selected' : undefined}
              width={OptionButtonWidth.Wide}
              htmlFor={inputId}
              data-test="optionButton"
              animationEnabled
              animationIndex={index}
            >
              <OptionButtonInput
                type="radio"
                name="options"
                defaultChecked={isSelected}
                value={choice.value}
                id={inputId}
              />
              <LeadingLabel aria-hidden="true">{index}</LeadingLabel>
              {choice.label}
            </NumberedOptionLabel>
          );
        })}
      </OptionFieldset>
    </div>
  );

  return <>{noWrapper ? content : <QuestionWrapper>{content}</QuestionWrapper>}</>;
};

const NumberedOptionLabel = styled(OptionButtonLabel)`
  position: relative;
`;

const LeadingLabel = styled.span`
  position: absolute;
  left: ${({ theme }) => theme.spacing._16px};
  top: 50%;
  transform: translateY(-50%);
  font-weight: bold;
  pointer-events: none;
`;
